<template>
  <div class="lesson-page" :style="{ backgroundImage: `url(${homeBg})` }">
    <div class="page-veil"></div>

    <div class="lesson-shell">
      <header class="lesson-header">
        <button class="back-btn" type="button" @click="logout">退出</button>
        <div class="header-copy">
          <p class="eyebrow">课程列表</p>
          <h1 class="book-title">{{ pageTitle }}</h1>
          <p class="class-meta">
            <span>{{ schoolName }}</span>
            <span class="dot-sep">·</span>
            <span>{{ className }}</span>
            <span class="dot-sep">·</span>
            <span class="student">{{ studentName }}</span>
          </p>
        </div>
      </header>

      <section class="cover-wrap" :class="{ placeholder: !cover }">
        <img v-if="cover" :src="cover" alt="" class="cover-img" />
        <div v-else class="cover-fallback">
          <span>YES</span>
          <p>本课封面</p>
        </div>
      </section>

      <section v-if="loading" class="center-state">
        <span class="spinner"></span>
        <p>加载课程中…</p>
      </section>

      <section v-else-if="loadError" class="center-state">
        <p>{{ loadError }}</p>
        <button class="pill-btn" type="button" @click="fetchLessons">重试</button>
      </section>

      <div v-else class="lesson-content">
        <div class="legend">
          <span><i class="swatch gray"></i>未开启</span>
          <span><i class="swatch red"></i>已开启</span>
          <span><i class="swatch green"></i>已学习</span>
          <span><i class="swatch grade">A</i>成绩</span>
        </div>

        <div class="lesson-list">
          <article
            v-for="(item, index) in lessons"
            :key="item.index"
            class="lesson-card"
            :class="{ locked: !item.unlocked, learned: item.learned }"
            :style="{ animationDelay: `${index * 45}ms` }"
            @click="goToLesson(item)"
          >
            <div class="lesson-main">
              <span class="lesson-index">{{ String(item.index + 1).padStart(2, '0') }}</span>
              <div>
                <span class="lesson-label">{{ item.title }}</span>
                <p class="lesson-hint">{{ lessonHint(item) }}</p>
              </div>
            </div>
            <span class="status-pair" aria-hidden="true">
              <span class="status-dot" :class="statusDotClass(item)"></span>
              <span class="grade-chip" :class="{ empty: !item.learned }">
                {{ item.learned ? item.grade : '' }}
              </span>
            </span>
          </article>
        </div>

        <p v-if="!lessons.length" class="empty-tip">暂无可用课程</p>
      </div>
    </div>
  </div>
</template>

<script>
import { getActivityLessons } from '@/api/activity'
import { ensureStudentSession, removeStudentSession } from '@/utils/auth'
import { resolveActivityId } from '@/utils/activity-route'
import homeBg from '@/assets/home-bg.jpg'

export default {
  name: 'LessonSelect',
  data() {
    return {
      homeBg,
      activityId: null,
      pageTitle: '',
      schoolName: '',
      className: '',
      studentName: '',
      cover: '',
      lessons: [],
      loading: true,
      loadError: ''
    }
  },
  created() {
    this.activityId = resolveActivityId(this.$route)
    this.fetchLessons()
  },
  methods: {
    async fetchLessons() {
      this.loading = true
      this.loadError = ''
      try {
        const session = ensureStudentSession(this.activityId)
        if (!session) {
          this.$router.replace(`/a/${this.activityId}`)
          return
        }
        const res = await getActivityLessons(this.activityId)
        const data = res.data
        this.pageTitle = data.bookTitle || '课程列表'
        this.schoolName = data.schoolName || ''
        this.className = data.className || ''
        this.studentName = data.studentName || ''
        this.cover = data.bookCover || ''
        this.lessons = data.lessons || []
      } catch (error) {
        this.loadError = error.message || '加载失败，请重试'
      } finally {
        this.loading = false
      }
    },
    lessonHint(item) {
      if (!item.unlocked) return '老师尚未开启'
      if (item.learned) return `最高等级 ${item.grade}`
      return '可以开始学习'
    },
    statusDotClass(item) {
      if (!item.unlocked) return 'gray'
      if (item.learned) return 'green'
      return 'red'
    },
    goToLesson(item) {
      if (!item.unlocked) {
        this.loadError = '该课程尚未开启'
        return
      }
      this.$router.push(`/a/${this.activityId}/lessons/${item.index}`)
    },
    logout() {
      removeStudentSession()
      this.$router.replace(`/a/${this.activityId}`)
    }
  }
}
</script>

<style scoped>
.lesson-page {
  --ink: #1c2a1a;
  --muted: #61705b;
  --line: rgba(28, 42, 26, 0.1);
  --panel: rgba(255, 252, 245, 0.94);
  --accent: #1aa39a;
  position: relative;
  min-height: 100vh;
  background-color: #dce8a8;
  background-size: cover;
  background-position: center top;
  background-repeat: no-repeat;
  overflow: hidden;
}

.page-veil {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.18) 0%, rgba(232, 240, 193, 0.45) 42%, rgba(220, 232, 168, 0.72) 100%);
  pointer-events: none;
}

.lesson-shell {
  position: relative;
  z-index: 1;
  max-width: 480px;
  margin: 0 auto;
  min-height: 100vh;
  padding: 18px 16px 28px;
}

.lesson-header {
  display: flex;
  flex-direction: column;
  gap: 14px;
  animation: fade-up 0.45s ease both;
}

.back-btn {
  align-self: flex-start;
  border: none;
  background: rgba(255, 255, 255, 0.88);
  color: var(--ink);
  font-size: 14px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 999px;
  box-shadow: 0 6px 16px rgba(28, 42, 26, 0.08);
  cursor: pointer;
}

.eyebrow {
  margin: 0 0 6px;
  font-size: 12px;
  letter-spacing: 0.12em;
  color: var(--muted);
  text-transform: uppercase;
}

.book-title {
  margin: 0;
  font-size: 26px;
  font-weight: 800;
  color: var(--ink);
  line-height: 1.25;
}

.class-meta {
  margin: 8px 0 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.5;
}

.student {
  color: var(--accent);
  font-weight: 700;
}

.dot-sep {
  margin: 0 4px;
  opacity: 0.5;
}

.cover-wrap {
  margin-top: 18px;
  height: 168px;
  border-radius: 22px;
  overflow: hidden;
  background: var(--panel);
  box-shadow: 0 16px 32px rgba(28, 42, 26, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.7);
  animation: fade-up 0.5s ease 0.05s both;
}

.cover-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.cover-fallback {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background:
    radial-gradient(circle at 30% 20%, rgba(255, 255, 255, 0.75), transparent 45%),
    linear-gradient(135deg, #e8f0c1 0%, #cfe3a0 100%);
  color: var(--ink);
}

.cover-fallback span {
  font-size: 28px;
  font-weight: 800;
  letter-spacing: 0.2em;
}

.cover-fallback p {
  margin: 0;
  font-size: 13px;
  color: var(--muted);
}

.lesson-content {
  margin-top: 18px;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  margin-bottom: 12px;
  padding: 0 4px;
  font-size: 12px;
  color: var(--muted);
}

.legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.swatch {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.swatch.gray { background: #9aa39a; }
.swatch.red { background: #e4574c; }
.swatch.green { background: #3eaf6b; }
.swatch.grade {
  width: auto;
  height: auto;
  border-radius: 0;
  font-weight: 800;
  color: #2f7de1;
}

.lesson-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: calc(100vh - 340px);
  overflow-y: auto;
  padding: 2px 2px 8px;
}

.lesson-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 16px;
  border-radius: 18px;
  background: var(--panel);
  border: 1px solid rgba(255, 255, 255, 0.75);
  box-shadow: 0 10px 24px rgba(28, 42, 26, 0.08);
  cursor: pointer;
  animation: fade-up 0.4s ease both;
  transition: transform 0.15s ease, box-shadow 0.2s ease;
}

.lesson-card:active {
  transform: scale(0.985);
}

.lesson-card.locked {
  cursor: not-allowed;
  opacity: 0.72;
  filter: grayscale(0.15);
}

.lesson-card.learned {
  box-shadow: 0 10px 24px rgba(62, 175, 107, 0.12);
}

.lesson-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.lesson-index {
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(232, 240, 193, 0.85);
  color: var(--ink);
  font-size: 14px;
  font-weight: 800;
}

.lesson-label {
  display: block;
  font-size: 17px;
  font-weight: 750;
  color: var(--ink);
}

.lesson-hint {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--muted);
}

.status-pair {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
}

.status-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.55);
}

.status-dot.gray { background: #9aa39a; }
.status-dot.red { background: #e4574c; }
.status-dot.green { background: #3eaf6b; }

.grade-chip {
  min-width: 28px;
  height: 28px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 800;
  color: #2f7de1;
  background: rgba(47, 125, 225, 0.1);
}

.grade-chip.empty {
  background: #d7ddd4;
  color: transparent;
  border-radius: 50%;
  min-width: 14px;
  width: 14px;
  height: 14px;
}

.center-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  min-height: 28vh;
  color: var(--muted);
}

.spinner {
  width: 28px;
  height: 28px;
  border: 3px solid rgba(26, 163, 154, 0.2);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.empty-tip {
  text-align: center;
  color: var(--muted);
  margin-top: 24px;
}

.pill-btn {
  border: none;
  background: linear-gradient(180deg, #24b7ad 0%, #12867f 100%);
  color: #fff;
  padding: 10px 24px;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 10px 20px rgba(18, 134, 127, 0.25);
}

@keyframes fade-up {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>



// WEBPACK FOOTER //
// src/views/LessonSelect.vue